import type { ITokensResponse } from './auth.interface';

import { AUTH_SESSION_EXPIRED_EVENT } from 'services/events';

// Distinct from token refresh: cached data belongs to a signed-in identity.
export const AUTH_SESSION_CHANGED_EVENT = 'auth-session-changed';

let revision = 0;
let knownAccessToken: string | null = null;
let knownRefreshToken: string | null = null;

// Explicit identity changes invalidate even a logout/login with identical tokens.
const discardSession = (): void => {
  revision += 1;
  knownAccessToken = localStorage.getItem('accessToken');
  knownRefreshToken = localStorage.getItem('refreshToken');
};

window.addEventListener(AUTH_SESSION_CHANGED_EVENT, discardSession);
window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, discardSession);
window.addEventListener('storage', event => {
  if (event.storageArea && event.storageArea !== localStorage) return;
  if (!event.key || event.key === 'accessToken' || event.key === 'refreshToken') {
    discardSession();
  }
});

export function getAuthSessionRevision(): number {
  if (
    knownAccessToken !== localStorage.getItem('accessToken') ||
    knownRefreshToken !== localStorage.getItem('refreshToken')
  ) {
    discardSession();
  }
  return revision;
}

export function isAuthSessionCurrent(owner: number): boolean {
  return getAuthSessionRevision() === owner;
}

// An owned token rotation continues the same session; unrelated token writes do not.
export function replaceAuthTokens(
  owner: number,
  expected: ITokensResponse,
  next: ITokensResponse,
): boolean {
  if (
    !isAuthSessionCurrent(owner) ||
    localStorage.getItem('accessToken') !== expected.accessToken ||
    localStorage.getItem('refreshToken') !== expected.refreshToken
  ) {
    return false;
  }
  localStorage.setItem('accessToken', next.accessToken);
  localStorage.setItem('refreshToken', next.refreshToken);
  knownAccessToken = next.accessToken;
  knownRefreshToken = next.refreshToken;
  return true;
}

export function notifyAuthSessionChanged(): void {
  window.dispatchEvent(new Event(AUTH_SESSION_CHANGED_EVENT));
}
